<script lang="ts">
    import { App, PRODUCT_VARIANT_DEFAULT_TITLE } from "@/constants";
    import { STATUS } from "@/constants/statuses";
    import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
    import { urlsBuilderContextToken } from "@/context_tokens";
    import { ListingAverageReviewsWidgetDefinition } from "@apps/reviews/definitions";
    import { reviewsApplicationLoggerToken } from "@apps/reviews/tokens";
    import WishlistAddToCartButton from "@apps/wishlist/components/WishlistAddToCartButton.svelte";
    import { BUTTON_VARIANT } from "@apps/wishlist/components/WishlistTheme/constants";
    import { wishlistThemeContextToken } from "@apps/wishlist/components/WishlistTheme/WIshlistThemeContextToken";
    import { WishlistItemEntity } from "@apps/wishlist/entities/WishlistItemEntity";
    import { WishlistWithItemsEntity } from "@apps/wishlist/entities/WishlistWithItemsEntity";
    import WishlistsBackInStockItemsQuery from "@apps/wishlist/queries/WishlistsBackInStockItemsQuery";
    import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
    import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { BaseModule } from "@modules/base_module/BaseModule";
    import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { ExtractModuleType } from "@modules/dynamic_import/types";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import { ProductsWithQuantityRulesQuery } from "@modules/products/queries/ProductsWithQuantityRulesQuery";
    import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
    import { dispatchSdkEvent } from "@modules/sdk/helpers";
    import { map } from "rxjs";
    import { createEventDispatcher } from "svelte";
    import { container } from "tsyringe";

    import ActionItem from "@components/Actions/ActionItem.svelte";
    import ActionItemLabel from "@components/Actions/ActionItemLabel.svelte";
    import ActionsList from "@components/Actions/ActionsList.svelte";
    import ActionsPopover from "@components/Actions/ActionsPopover.svelte";
    import { BOTTOM_RIGHT } from "@components/Actions/positions";
    import Badge from "@components/Badge/Badge.svelte";
    import Button from "@components/Buttons/Button.svelte";
    import ButtonWithIcon from "@components/Buttons/ButtonWithIcon.svelte";
    import Card from "@components/Card/Card.svelte";
    import Dropdown from "@components/forms/Select/Dropdown.svelte";
    import Icon from "@components/Icon.svelte";
    import ThreedotVerticalIcon from "@components/icons/ThreedotVerticalIcon.svelte";
    import Image from "@components/Image/Image.svelte";
    import Money from "@components/Money.svelte";
    import T from "@components/T.svelte";
    import Widget from "@components/Widget.svelte";

    import { wishlistPageWidgetContext } from "../WishlistPageWidgetContext";
    import WishlistPageItemSkeleton from "./WishlistPageItemSkeleton.svelte";

    enum ActionListView {
        ACTIONS = "ACTIONS",
        MOVEWISHLISTS = "MOVEWISHLISTS",
    }

    let currentView = ActionListView.ACTIONS;
    export let item: WishlistItemEntity;
    export let wishlist: WishlistWithItemsEntity;
    export let wishlists: WishlistWithItemsEntity[];
    export let productsQuery: ProductsQuery;
    export let productsWithQuantityRulesQuery: ProductsWithQuantityRulesQuery;
    export let wishlistsBackInStockItemsQuery: WishlistsBackInStockItemsQuery;
    export let isSharedItem: boolean;
    export let criticalNamespace: string;
    export let namespace: string;
    export let dynamicImportManager: DynamicImportManager;
    export let isCartAvailable: boolean;

    const dispatch = createEventDispatcher();

    const urlsBuilder = getTypedContext(urlsBuilderContextToken);

    const modulesStatusesProvider =
        container.resolve(BaseModule).exports.modulesStatusesProvider;
    const reviewsAppStatus$ = modulesStatusesProvider.selectModuleStatus(
        App.Reviews
    );

    const productIdManager = new ProductIdManager();
    productIdManager.registerProvider(
        new ProductIdFromStaticProvider(item.productId)
    );

    const targetWishlists = wishlists.filter((wishlist) =>
        wishlist.items.every(
            (currentItem) => currentItem.productId != item.productId
        )
    );

    const { cartQuery, cartService } = getTypedContext(
        wishlistPageWidgetContext
    );

    const productAndVariantInfo$ = productsQuery.selectProductAndVariant(
        item.productId,
        item.variantId
    );

    const productInfo$ = productsQuery.selectProduct(item.productId);
    const filteredProductVariants$ = productInfo$.pipe(
        map((product) => {
            if (!product) return [];

            const wishlistVariantIds = new Set(
                wishlist.items.map((item) => item.variantId)
            );

            return product.variants.filter(
                (variant) =>
                    variant.id === item.variantId ||
                    !wishlistVariantIds.has(variant.id)
            );
        })
    );

    const itemAvailableQuantity$ = productAndVariantInfo$.pipe(
        map((productInfo) => productInfo.variant?.quantityAvailable || 1)
    );

    const productUrl$ = productAndVariantInfo$.pipe(
        map((productInfo) => {
            if (!productInfo.product) return null;

            return urlsBuilder.createProductUrl(
                productInfo.product.handle,
                productInfo.variant?.id
            );
        })
    );

    const imageUrl$ = productAndVariantInfo$.pipe(
        map((productAndVariantInfo) => {
            const [variantImage, productFeaturedImage, productFirstImage] = [
                productAndVariantInfo?.variant?.image?.url,
                productAndVariantInfo?.product?.featuredImage?.url,
                productAndVariantInfo?.product?.images[0].url,
            ];

            return variantImage || productFeaturedImage || productFirstImage;
        })
    );

    const productOptions$ = productAndVariantInfo$.pipe(
        map((productInfo) =>
            productInfo.variant?.options.filter(
                (option) => option.value !== PRODUCT_VARIANT_DEFAULT_TITLE
            )
        )
    );

    const soldOutStatus$ = productAndVariantInfo$.pipe(
        map((productAndVariantInfo) => {
            return !productAndVariantInfo.variant?.availableForSale;
        })
    );

    const isProductBackInStock$ =
        wishlistsBackInStockItemsQuery.selectIsProductBackInStock(
            item.productId,
            item.variantId
        );

    const isProductOnSale$ = productAndVariantInfo$.pipe(
        map((productAndVariantInfo) => {
            return !!(
                productAndVariantInfo.variant?.compareAtPrice &&
                productAndVariantInfo.variant?.compareAtPrice.amount >
                    productAndVariantInfo.variant?.price.amount
            );
        })
    );

    const handleDeleteItem = () => {
        dispatch("deleteItem");
    };

    const handleMoveItem = (targetWishlistId: number) => {
        dispatch("moveItem", { targetWishlistId });
    };

    const handleUpdateItemVariant = (newVariantId: number) => {
        dispatch("updateItemVariant", { newVariantId });
    };

    const handleOpenActions = () => {
        currentView = ActionListView.ACTIONS;
    };

    const itemInCartStatus$ = cartQuery
        .selectQuantityByVariantId(item.variantId)
        .pipe(map((quantity) => quantity > 0));

    const quantityRule$ = productsWithQuantityRulesQuery.selectQuantityRule(
        item.productId,
        item.variantId
    );

    const handleAddToCart = (itemQuantity: number) => {
        cartService.addToCart([
            {
                variantID: item.variantId,
                quantity: $quantityRule$?.minimum || itemQuantity,
            },
        ]);
    };

    type ListingAverageReviewsWidgetType = ExtractModuleType<
        typeof ListingAverageReviewsWidgetDefinition
    >;

    let ListingAverageReviewsWidget:
        | ListingAverageReviewsWidgetType["default"]
        | null = null;

    const importListingAverageReviewsWidget = async () => {
        if (ListingAverageReviewsWidget) return;

        try {
            ListingAverageReviewsWidget =
                await dynamicImportManager.importDefault(
                    ListingAverageReviewsWidgetDefinition,
                    true
                );
        } catch (error) {
            console.error(
                "Failed to load ListingAverageReviewsWidget module",
                error
            );
        }
    };

    $: if ($reviewsAppStatus$ === MODULE_STATUS.INITIALIZED) {
        importListingAverageReviewsWidget();
    }

    const handleListingAverageReviewsWidgetInit = () => {
        dispatchSdkEvent(
            new WidgetInjectedSdkEvent(
                REVIEWS_WIDGET_NAMES.LISTING_AVERAGE_REVIEWS_WIDGET
            )
        );
    };

    const addToCartLoadingStatus$ = cartQuery.selectEditItemStatus(
        item.variantId
    );

    const loading$ = addToCartLoadingStatus$.pipe(
        map((status) =>
            [STATUS.LOADING, STATUS.INITIAL_LOADING].includes(status)
        )
    );

    const wishlistBranding = getTypedContext(wishlistThemeContextToken);
</script>

<div>
    {#if !$productAndVariantInfo$.product}
        <Card>
            <WishlistPageItemSkeleton />
        </Card>
    {:else}
        <div
            class="gw-wl-page-item"
            data-gw-selector="wishlist-item"
            data-gw-product-id={item.productId}
            data-gw-variant-id={item.variantId}
            data-gw-wishlist-id={item.wishlistId}
        >
            {#if !isSharedItem}
                <div class="gw-wl-page-item-actions">
                    <ActionsPopover
                        on:close={handleOpenActions}
                        position={BOTTOM_RIGHT}
                    >
                        <div slot="activator">
                            <ButtonWithIcon>
                                <Icon color="#B1B5C3">
                                    <ThreedotVerticalIcon />
                                </Icon>
                            </ButtonWithIcon>
                        </div>
                        <ActionsList>
                            <ActionItem
                                color="#EF466F"
                                on:click={() => handleDeleteItem()}
                            >
                                <span class="gw-wl-action-item">
                                    <T
                                        {namespace}
                                        translationKey="delete_wishlist_item_action_label"
                                    />
                                </span>
                            </ActionItem>
                            {#if wishlists.length > 1}
                                <ActionItemLabel>
                                    <T
                                        {namespace}
                                        translationKey="move_wishlist_item_label"
                                    />
                                </ActionItemLabel>
                                {#each targetWishlists as wishlist}
                                    <ActionItem
                                        on:click={() =>
                                            handleMoveItem(wishlist.id)}
                                    >
                                        {wishlist.title}
                                    </ActionItem>
                                {/each}
                            {/if}
                        </ActionsList>
                    </ActionsPopover>
                </div>
            {/if}

            <a href={$productUrl$} class="gw-wl-page-item__image">
                <Image
                    objectFit="contain"
                    ratio={1}
                    src={urlsBuilder.createProductImageUrl($imageUrl$ || "")}
                    alt={$productAndVariantInfo$.product?.title || ""}
                />
                <div class="gw-wl-drawer-item-badge">
                    {#if $isProductOnSale$}
                        <Badge
                            variant="info"
                            textColor="#fff"
                            backgroundColor="#45B26B"
                        >
                            <T {namespace} translationKey="on_sale" />
                        </Badge>
                    {/if}
                    {#if $isProductBackInStock$}
                        <Badge
                            variant="info"
                            textColor="#fff"
                            backgroundColor="#3772FF"
                        >
                            <T {namespace} translationKey="back_in_stock" />
                        </Badge>
                    {/if}
                </div>
            </a>

            <div class="gw-wl-page-item-header">
                <a href={$productUrl$} class="gw-wl-page-item__title">
                    {$productAndVariantInfo$.product?.title}
                </a>

                <div class="gw-wl-page-item-content__price-block">
                    {#if $isProductOnSale$}
                        <div class="gw-wl-page-item-compare-price">
                            <Money
                                with_currency
                                amount={$productAndVariantInfo$?.variant
                                    ?.compareAtPrice?.amount || 0}
                            />
                        </div>
                    {/if}

                    <div class="gw-wl-page-item-price">
                        <Money
                            with_currency
                            amount={$productAndVariantInfo$?.variant?.price
                                .amount || 0}
                        />
                    </div>
                </div>
            </div>

            {#if $reviewsAppStatus$ === MODULE_STATUS.INITIALIZED && ListingAverageReviewsWidget}
                <Widget
                    class="gw-wl-page-item__listing-avg-reviews-container"
                    widget={ListingAverageReviewsWidget}
                    params={[
                        {
                            isWidgetShadowRootEnabled: true,
                        },
                        container.resolve(reviewsApplicationLoggerToken),
                        productIdManager,
                    ]}
                    on:widgetInit={handleListingAverageReviewsWidgetInit}
                />
            {/if}

            {#if $productOptions$?.length}
                <div class="gw-wl-page-item-content__options-wrapper">
                    {#if $filteredProductVariants$?.length > 1}
                        <Dropdown
                            selectClass="gw-wl-page-item-content__select_options"
                            items={$filteredProductVariants$.map((variant) => ({
                                value: variant.id,
                                label: variant.title,
                            }))}
                            value={item.variantId}
                            on:change={(e) => {
                                const newVariantId = e.detail.value;
                                if (newVariantId === item.variantId) return;

                                handleUpdateItemVariant(newVariantId);
                            }}
                        />
                    {/if}

                    <table class="gw-wl-page-item-content__options">
                        {#each $productOptions$ as option}
                            <tr class="gw-wl-page-item-content__options-item">
                                <td
                                    class="gw-wl-page-item-content__options-item-name"
                                    >{option.name}:</td
                                >
                                <td
                                    class="gw-wl-page-item-content__options-item-value"
                                    >{option.value}</td
                                >
                            </tr>
                        {/each}
                    </table>
                </div>
            {/if}

            {#if isCartAvailable}
                <div class="gw-wl-page-item__cart-btn">
                    {#if !$soldOutStatus$ && !$itemInCartStatus$}
                        <WishlistAddToCartButton
                            on:addToCart={(e) => handleAddToCart(e.detail)}
                            loading={$loading$}
                            {namespace}
                            translationKey="add_quantity_to_cart_button_label"
                            availableQuantity={$itemAvailableQuantity$}
                        />
                    {:else if $itemInCartStatus$}
                        <Button
                            block
                            url={urlsBuilder.createCartUrl()}
                            variant={BUTTON_VARIANT[
                                wishlistBranding.wishlistDrawerAndPageSettings
                                    .buttonsStyle
                            ]}
                            rounded
                        >
                            <T
                                namespace={criticalNamespace}
                                translationKey="show_in_cart_button_label"
                            />
                        </Button>
                    {:else if $soldOutStatus$ && !$itemInCartStatus$}
                        <Button
                            block
                            variant={BUTTON_VARIANT[
                                wishlistBranding.wishlistDrawerAndPageSettings
                                    .buttonsStyle
                            ]}
                            disabled
                            rounded
                        >
                            <T
                                namespace={criticalNamespace}
                                translationKey="sold_out_label"
                            />
                        </Button>
                    {/if}
                </div>
            {/if}
        </div>
    {/if}
</div>

<style lang="postcss">
    .gw-wl-page-item {
        position: relative;
        height: 100%;
    }

    .gw-wl-page-item-actions {
        position: absolute;
        top: 0;
        left: 10px;
        z-index: 99;
    }
    .gw-wl-page-item__image {
        --gw-image-backdrop-color: transparent;
        position: relative;
    }

    .gw-wl-drawer-item-badge {
        position: absolute;
        left: 8px;
        bottom: 8px;
        display: flex;
        gap: 5px;
        flex-direction: column;
        :global(.gw-badge) {
            width: fit-content;
            padding: 2px 8px;
            border-radius: 10px;
            line-height: 12px;
        }
    }

    .gw-wl-page-item-header {
        margin-top: 12px;
        display: flex;
        align-items: start;
        justify-content: space-between;
    }

    .gw-wl-page-item__title {
        display: -webkit-box;
        font-size: 0.875em;
        line-height: 1.5em;
        color: inherit;
        text-decoration: none;
        text-overflow: ellipsis;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    :global(.gw-wl-page-item__listing-avg-reviews-container) {
        margin-left: -2px;
    }

    .gw-wl-action-item {
        font-size: 10px;
        font-weight: 600;
    }

    .gw-wl-page-item-content__price-block {
        display: flex;
        flex-wrap: wrap;
        gap: 3px;
        justify-content: center;
        align-items: center;
        line-height: 0;
        margin: 0.12em 0 0.3em 0;
    }

    .gw-wl-page-item-compare-price {
        display: inline-block;
        font-size: 0.625em;
        line-height: 0.75em;
        text-decoration: line-through;
        color: #b1b5c3;
    }

    .gw-wl-page-item-price {
        font-size: 0.75em;
        font-weight: 600;
        line-height: 0.75em;
        padding: 0.25em 0.375em;
        display: inline-block;
        border-radius: 0.25em;
        color: #45b26b;
        border: 1px solid rgba(69, 178, 107, 0.4);
    }

    .gw-wl-page-item-actions {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;
    }

    .gw-wl-page-item__cart-btn {
        display: flex;
        justify-content: center;
        margin-top: 0.7em;

        & :global(.gw-btn.gw-button.--outlined) {
            color: var(--gw-theme-main-text-primary);
        }
    }

    .gw-wl-page-item-content__options {
        border-spacing: 0;

        &-wrapper {
            margin-top: 6px;

            :global(.gw-wl-page-item-content__select_options.gw-select) {
                --listTop: 30px;
                border-radius: 5px;
                margin: 5px 0;
                padding: 5px;
            }
        }

        &-item {
            &-name {
                color: #777e90;
                font-size: 0.6em;
                font-weight: 500;
                padding: 0 6px 0 0;
            }
            &-value {
                color: #141416;
                font-size: 0.6em;
                font-weight: 500;
                padding: 0;
            }
        }
    }
</style>
